import { oneOf, string, func, node, shape } from 'prop-types';
import React from 'react';

import ClassNames from 'classnames';

import BaseComponent from '../.internal/BaseComponent';
import List from '../.internal/list/List';

import './Dropdown.scss';

export default class Dropdown extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      menuVisibility: 'hidden'
    };
    this.dropdownRef = React.createRef();
    this.menuRef = React.createRef();
    this.listRef = React.createRef();
  }

  dropdownEvent = () => {
    const { menuVisibility } = this.state;

    this.menuRef.current.style.display = 'block';

    this.setState({
      menuVisibility: menuVisibility === 'visible' ? 'hidden' : 'visible'
    });
  };

  menuOutOfFocusClickEvent = e => {
    const { menuRef, dropdownRef } = this;
    const { target } = e;

    if (
      menuRef.current &&
      dropdownRef.current &&
      // TODO: Better to use forwarded refs here. Please plan out with the team which components
      // should and shouldn't use forwarded refs. -jaimehing
      !dropdownRef.current.ref.current.contains(target)
    ) {
      this.setState({ menuVisibility: 'hidden' });
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevState.menuVisibility !== this.state.menuVisibility) {
      if (this.state.menuVisibility === 'visible') {
        const menuRefCurrent = this.menuRef.current;
        const listCurrent = this.listRef.current.ref.current;
        const dropdownRefCurrent = this.dropdownRef.current.ref.current;
        const listOffsetHeight = listCurrent.offsetHeight;
        const listOffsetWidth = listCurrent.offsetWidth;
        const dropdownDimension = dropdownRefCurrent.getBoundingClientRect();
        const { position } = this.props;
        const arrPosition = position.split('-');

        const yCoordinates = {
          top: {
            basis: 'top',
            position: dropdownDimension.top - listOffsetHeight - 10
          },
          bottom: {
            basis: 'top',
            position: dropdownDimension.bottom + 10
          }
        }[arrPosition[0]];

        const xCoordinates = {
          top: {
            basis: 'left',
            position:
              dropdownDimension.left -
              (listOffsetWidth - dropdownRefCurrent.offsetWidth) / 2
          },
          'top-right': {
            basis: 'left',
            position: dropdownDimension.right - listOffsetWidth
          },
          'top-left': {
            basis: 'left',
            position: dropdownDimension.left
          },
          bottom: {
            basis: 'left',
            position:
              dropdownDimension.left -
              (listOffsetWidth - dropdownRefCurrent.offsetWidth) / 2
          },
          'bottom-right': {
            basis: 'left',
            position: dropdownDimension.right - listOffsetWidth
          },
          'bottom-left': {
            basis: 'left',
            position: dropdownDimension.left
          }
        }[position];

        menuRefCurrent.style[yCoordinates.basis] = `${yCoordinates.position}px`;
        menuRefCurrent.style[xCoordinates.basis] = `${xCoordinates.position}px`;
      }
    }
  }

  dropdownTransitionEvent = e => {
    const elem = e.currentTarget;

    if (e.propertyName === 'opacity') {
      if (this.state.menuVisibility === 'hidden') {
        elem.style.display = 'none';
      }
    }
  };

  onDropdownMouseOutEvent = () => {
    this.setState({ menuVisibility: 'hidden' });
  };

  onMenuFocusEvent = () => {
    if (this.props.trigger === 'hover') {
      this.setState({ menuVisibility: 'visible' });
    }
  };

  componentDidMount() {
    if (typeof document !== 'undefined') {
      document.addEventListener('mousedown', this.menuOutOfFocusClickEvent);
    }

    this.menuRef.current.addEventListener('mouseover', this.onMenuFocusEvent);

    this.menuRef.current.addEventListener(
      'transitionend',
      this.dropdownTransitionEvent
    );
  }

  componentWillUnmount() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('mousedown', this.menuOutOfFocusClickEvent);
    }

    this.menuRef.current.removeEventListener(
      'transitionend',
      this.dropdownTransitionEvent
    );
    this.menuRef.current.removeEventListener(
      'mouseover',
      this.onMenuFocusEvent
    );
  }

  getTriggerElementEvents = trigger => {
    const triggerElementEvents = {
      hover: {
        onMouseOver: this.dropdownEvent,
        onMouseOut: this.onDropdownMouseOutEvent
      },
      click: {
        onClick: this.dropdownEvent
      }
    };

    return triggerElementEvents[trigger];
  };

  render() {
    const { trigger, children, menu, className } = this.props;
    const { menuVisibility } = this.state;

    const triggerElementEvents = this.getTriggerElementEvents(trigger);

    const dropdownMenuContainerClass = ClassNames('Dropdown-menu-container', {
      visible: menuVisibility === 'visible'
    });

    return (
      <>
        {React.cloneElement(children, {
          ...triggerElementEvents,
          ref: this.dropdownRef
        })}
        <div className={dropdownMenuContainerClass} ref={this.menuRef}>
          <List
            dense
            className={ClassNames('Dropdown-menu', className)}
            ref={this.listRef}
          >
            {menu}
          </List>
        </div>
      </>
    );
  }
}

Dropdown.propTypes = {
  style: shape({}),
  id: string,
  className: string,
  onChange: func,
  trigger: oneOf(['click', 'hover']),
  position: oneOf(Dropdown.positionOptions),
  children: node
};

Dropdown.positionOptions = [
  'top',
  'top-left',
  'top-right',
  'bottom',
  'bottom-left',
  'bottom-right'
];

Dropdown.defaultProps = {
  style: {},
  id: undefined,
  className: undefined,
  trigger: 'click',
  position: 'top-left'
};
